// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ToggleButton.tsx
 * @input Uses Button, React, StyleX, theme tokens
 * @output Exports ToggleButton component and types
 * @position Thin wrapper over Button; adds controlled toggle pattern
 *
 * ToggleButton wraps Button with `isPressed` and adds:
 * - `onPressedChange` controlled toggle callback
 * - `pressedIcon` for outline-to-filled icon swap
 * - Font weight shift on press with width reservation to prevent layout shift
 * - Group integration via ToggleButtonGroupContext
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/ToggleButton/index.ts (exports if types change)
 * - /apps/storybook/stories/ToggleButton.stories.tsx
 * - /packages/cli/templates/blocks/components/ToggleButton/ (showcase blocks)
 */
import React, { useOptimistic } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, fontWeightVars } from "../theme/tokens.stylex.js";
import { Button } from "../Button/index.js";
import { useToggleButtonGroup } from "./ToggleButtonGroup.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Styles
// =============================================================================

/**
 * Font weight shift on press with width reservation trick.
 * A hidden span renders the same text at semibold weight to reserve
 * the wider width, preventing layout shift when toggling.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const pressedStyles = {
  background: {
    kWkggS: "xi89dp7",
    $$css: true
  }
};
// =============================================================================
// Props
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * A button that toggles between pressed and unpressed states.
 * Thin wrapper over Button — adds controlled toggle pattern,
 * icon swap, and font weight emphasis.
 *
 * Use for toolbar actions, view mode switches, and formatting controls.
 * For on/off settings, use Switch instead.
 *
 * Works standalone (with `isPressed`/`onPressedChange`) or inside
 * ToggleButtonGroup (which controls selection via `value`).
 *
 * @example
 * ```
 * const [isBold, setIsBold] = useState(false);
 * <ToggleButton
 *   label="Bold"
 *   icon={<BoldIcon />}
 *   isPressed={isBold}
 *   onPressedChange={setIsBold}
 * />
 * ```
 */
export function ToggleButton({
  ref,
  label,
  isPressed: isPressedProp,
  onPressedChange: onPressedChangeProp,
  pressedChangeAction,
  size: sizeProp,
  isDisabled: isDisabledProp = false,
  isLoading = false,
  icon,
  isIconOnly = false,
  pressedIcon,
  children,
  tooltip,
  value,
  xstyle,
  className: _className,
  style,
  ...props
}) {
  const group = useToggleButtonGroup();
  const committedPressed = group && value != null ? group.selectedValues.has(value) : isPressedProp ?? false;
  const size = sizeProp ?? group?.size ?? 'md';
  const isDisabled = group?.isDisabled ?? isDisabledProp;

  // Track the pressed state optimistically so the button reflects the intended
  // state immediately while an async action is pending. The optimistic update
  // is applied inside Button's clickAction transition (below), satisfying
  // React's "useOptimistic must run in a transition" rule.
  const [optimisticPressed, setOptimisticPressed] = useOptimistic(committedPressed);
  const isPressed = optimisticPressed;

  // Next state derives from the *optimistic* value, not the committed one, so a
  // re-click while an action is pending reads the in-flight intent and toggles
  // (true -> false -> true) rather than recomputing from the stale committed
  // state. handleClick and clickAction share this so the callback and the
  // optimistic update always agree within a single click.
  const nextPressed = !isPressed;
  const resolvedIcon = isPressed && pressedIcon ? pressedIcon : icon;

  // Synchronous part of the toggle. Button calls onClick before clickAction and
  // skips clickAction when the event is defaultPrevented, so calling
  // preventDefault() inside onPressedChange opts out of pressedChangeAction.
  const handleClick = event => {
    if (isDisabled) {
      return;
    }
    if (group && value != null) {
      // Group mode delegates selection to the group; no async-action path.
      group.toggle(value);
      event.preventDefault();
      return;
    }
    onPressedChangeProp?.(nextPressed, event);
  };

  // Async part of the toggle, run inside Button's transition (so the optimistic
  // update is valid and Button's isPending drives the pending spinner). Skipped
  // automatically by Button when handleClick called preventDefault, and never
  // wired in group mode (where there is no async action). Button is rendered
  // interruptible, so it stays clickable while pending and a re-click starts a
  // fresh transition that interrupts the previous one.
  const clickAction = group && value != null ? undefined : async () => {
    setOptimisticPressed(nextPressed);
    await pressedChangeAction?.(nextPressed);
  };

  // isIconOnly prop is the source of truth for icon-only rendering.
  const labelContent = children != null ? /*#__PURE__*/_jsxs("span", {
    ...{
      className: "x3nfvp2 xdt5ytf x6s0dn4 xl56j7k"
    },
    children: [/*#__PURE__*/_jsx("span", {
      ...{
        0: {},
        1: {
          className: "x2mo6ok"
        }
      }[!!isPressed << 0],
      children: children
    }), /*#__PURE__*/_jsx("span", {
      ...{
        className: "x1lliihq x2mo6ok xqtp20y xb3r6kr xlshs6z x47corl"
      },
      "aria-hidden": "true",
      children: children
    })]
  }) : !isIconOnly ? /*#__PURE__*/_jsxs("span", {
    ...{
      className: "x3nfvp2 xdt5ytf x6s0dn4 xl56j7k"
    },
    children: [/*#__PURE__*/_jsx("span", {
      ...{
        0: {},
        1: {
          className: "x2mo6ok"
        }
      }[!!isPressed << 0],
      children: label
    }), /*#__PURE__*/_jsx("span", {
      ...{
        className: "x1lliihq x2mo6ok xqtp20y xb3r6kr xlshs6z x47corl"
      },
      "aria-hidden": "true",
      children: label
    })]
  }) : undefined;
  return /*#__PURE__*/_jsx(Button, {
    ref: ref,
    label: label,
    variant: "ghost",
    size: size,
    isDisabled: isDisabled,
    isLoading: isLoading,
    isInterruptible: true,
    isIconOnly: isIconOnly,
    "aria-pressed": isPressed,
    icon: resolvedIcon,
    tooltip: tooltip,
    ...themeProps('toggle-button', {
      isPressed: isPressed ? 'true' : 'false'
    }),
    xstyle: [isPressed ? pressedStyles.background : undefined, xstyle],
    style: style,
    onClick: handleClick,
    clickAction: clickAction,
    ...props,
    children: labelContent
  });
}
ToggleButton.displayName = 'ToggleButton';